import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FiClock, FiCheckSquare, FiFileText, FiSend, FiList } from 'react-icons/fi';
import './ActivityLog.css';

const ActivityLog = ({ token }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchActivityLog();
  }, []);

  const fetchActivityLog = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/activity-log`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setLogs(data.logs || []);
      }
    } catch (error) {
      console.error('Failed to fetch activity log:', error);
    } finally {
      setLoading(false);
    }
  };

  const getIcon = (entityType) => {
    switch (entityType) {
      case 'task': return <FiCheckSquare size={18} />;
      case 'global_task': return <FiList size={18} />;
      case 'rfi': return <FiFileText size={18} />;
      case 'submittal': return <FiSend size={18} />;
      default: return <FiClock size={18} />;
    }
  };

  const getActionColor = (actionType) => {
    switch (actionType) {
      case 'created': return '#1DBF73';
      case 'status_changed': return '#0F0F10';
      case 'updated': return '#F4A340';
      case 'deleted': return '#E24B5B';
      case 'assigned': return '#9B6BFF';
      default: return '#4B4B4B';
    }
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  if (loading) return <div className="loading-spinner"></div>;

  return (
    <div className="activity-log-page">
      <div className="activity-log-header">
        <h1 className="activity-log-title">Activity Log</h1>
        <p className="activity-log-subtitle">Last 100 changes in your workspace</p>
      </div>

      <div className="activity-log-list">
        {logs.length === 0 ? (
          <div className="empty-state">
            <FiClock size={48} style={{ color: 'var(--text-tertiary)', marginBottom: '16px' }} />
            <h3>No Activity Yet</h3>
            <p>Activity will appear here as your team works on tasks, RFIs, and submittals</p>
          </div>
        ) : (
          logs.map((log) => (
            <div key={log.id} className="activity-log-item" data-testid="activity-log-item">
              <div 
                className="activity-icon" 
                style={{ backgroundColor: `${getActionColor(log.actionType)}20`, color: getActionColor(log.actionType) }}
              >
                {getIcon(log.entityType)}
              </div>
              <div className="activity-content">
                <div className="activity-title">
                  <span className="activity-action" style={{ color: getActionColor(log.actionType) }}>
                    {log.actionType.replace('_', ' ').toUpperCase()}
                  </span>
                  <span className="activity-entity-type">{log.entityType.replace('_', ' ')}</span>
                  <span className="activity-entity-name">"{log.entityName}"</span>
                </div>
                {log.details && (
                  <div className="activity-details">{log.details}</div>
                )}
                <div className="activity-time">{formatTime(log.createdAt)}</div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default ActivityLog;